// global
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
// components
import { Table, Button, Popup, Image } from 'semantic-ui-react';
import PopupEditPrescription from "app/ui/pages/monitor/PopupEditPrescription";
// icons
import ic_flow_alert from 'app/_assets/img/ic_flow_alert.png';
import DeviceIcons from "app/ui/pages/monitor/monitorTableBody/monitorTableRow/deviceIcons/DeviceIcons";
import CylinderIcons from "app/ui/pages/monitor/monitorTableBody/monitorTableRow/cylinderIcons/CylinderIcons";
// actions
import {
    addRestitution,
    removeRestitution,
    openDetail,
    updatePrescription,
    updateView,
    updateMapParams } from 'app/redux/actions/monitor';
// utils
import t from 'app/lib/i18n';
import { addMinutes } from 'date-fns';
import { formatNumber } from 'app/lib/helper';
import diffBetweenDates from 'app/lib/dateHelper';
// consts
import { TYPE as CLIENT_TYPE } from 'salvus-shared/consts/client';
import { PROFILE } from 'salvus-shared/consts/account';
import { TYPE as DEVICE_TYPE } from 'salvus-shared/consts/device';
// stylesheet
import './MonitorTableRow.scss';

function MonitorTableRow(props) {
    const dispatch = useDispatch();

    const [state, setState] = useState({ onHover: false });

    const isSalvusUser = account => {
        if (!account) return false;

        const isSalvusClient = account.client.type === CLIENT_TYPE.SALVUS;
        const isAccountProfileDefault = account.profile === PROFILE.DEFAULT;

        if (isSalvusClient && isAccountProfileDefault) {
            return true;
        }

        return false;
    }

    const onConfirmPrescription = value => dispatch(updatePrescription(props.row.id, value));

    // Handler for clicks in the plus button
    const onAddClicked = e => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(addRestitution(props.row.id));
    };

    // Handler for clicks in the minus button
    const onRemoveClicked = e => {
      e.preventDefault();
      e.stopPropagation();
      dispatch(removeRestitution(props.row.id));
    };

    // Handler for the click in the monitor row
    const onClickRow = id => () => dispatch(openDetail(id));

    // Handler for the click in the edit patient on popup edit prescription
    const onEditPatient = id => dispatch(openDetail(id));

    // Handler for when the mouse enters the row
    const onMouseEnter = () => setState({ onHover: true });

    // Handler for when the mouse leaves the row
    const onMouseLeave = e => {
        // BUG: when clicking fast the mouse leave event is trigged by the child and not the parent
        if (e.target.nodeName === 'I' || e.target.nodeName === 'BUTTON' || e.currentTarget.nodeName !== 'TR') {
            return;
        }
        setState({ onHover: false });
    };

    // Helper fuction to check if the flwo is valid
    const isFlowValid = (flow, prescription) => {
        if (flow === '-') {
            return true;
        }
        const percent = (prescription * 15) / 100;
        return flow > prescription - percent && flow < prescription + percent;
    }

    // Helper function to format the end time
    const formatEndTime = endTime => {
        if (endTime === '-' || endTime === 0) {
            return '--';
        }
        const now = new Date();
        const end = addMinutes(now, endTime);
        return diffBetweenDates(end, now);
    }

    // Helper function to display the flow
    const formatFlow = flow => {
        if (flow === '-') {
            return '0,0';
        }
        return formatNumber(flow);
    }

    const showEndTime = (endTime, expectedEndTime) => {
        if (endTime === '-') {
            return formatEndTime(expectedEndTime);
        } else {
            return formatEndTime(endTime);
        }
    }

    const toMap = row => e => {
        e.preventDefault();
        e.stopPropagation();

        const latitude = row.device.latitude ? row.device.latitude : row.latitude;
        const longitude = row.device.longitude ? row.device.longitude : row.longitude;
        const center = { lat: latitude, lng: longitude };
        const zoom = 15;

        dispatch(updateView('map'));

        if (latitude && longitude) dispatch(updateMapParams({ center, zoom }));
    };
    
    let { onHover } = state;
    const { row, account } = props;
    const clsAlert = row.isOffline && row.alertBattery ? 'crit-alert' : '';
    const className = ['table-row', clsAlert].join(' ');
    const validFlow = isFlowValid(row.flow, row.prescription);

    console.log('row: ', row)

    return (
        <Table.Row 
            className={className}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClickRow(row.id)}
        >
            <Table.Cell textAlign="center" className="period-type" width="1">
                {t(`patient_period_type_short_${row.periodType}`)}
            </Table.Cell>

            <Table.Cell width="5" className="wrap">
                <span className="uppercase">{row.name}</span> <br />
                <span className="patient-info">
                <span className="bold">{row.phone1}</span>
                    {` | ${row.serviceNumber} ${row.healthInsurence}`}
                </span>
            </Table.Cell>

            <Table.Cell textAlign="center">
                <Button circular icon="location arrow" size="small" id={'monitor-button-device'} onClick={toMap(row)} />
            </Table.Cell>

            <Table.Cell textAlign="center" className="prescription" width="2">
                {formatNumber(row.prescription)}
            </Table.Cell>

            <Table.Cell textAlign="center" className={validFlow ? 'flow' : 'flow red-label'} width="2">
                <div className="flex flex-center">
                    {!validFlow && (
                        <Popup trigger={<Image src={ic_flow_alert} />} content="A litragem desse paciente não está de acordo com sua prescrição." />
                    )}
                    {formatFlow(row.flow)}
                </div>
            </Table.Cell>

            <Table.Cell
                textAlign="center"
                className={row.alertEndTime ? row.endTime < 720 ? 'duration red-alert blinking' : 'duration red-alert' : row.warningEndTime ? 'duration yellow-alert' : 'duration'}
                width="2"
            >
                <span>
                    <div className="flex-col">
                        <div className="flex flex-center">
                            {row.alertPress && (
                                <Popup
                                    trigger={<Image src={ic_flow_alert} />}
                                    content="Plug de pressão não está conectado. Isso pode afetar a estimativa de término."
                                />
                            )}
                            {row.device.type === DEVICE_TYPE.CONCENTRATOR ? '--' : showEndTime(row.endTime, row.expectedEndTime)}
                        </div>
                    </div>
                </span>
            </Table.Cell>

            <Table.Cell textAlign="center" className="device">
                <DeviceIcons row={row} />
            </Table.Cell>

            <Table.Cell>
                <div className="cilinders-container flex">{
                    <CylinderIcons
                        cylinderTypes={row.cylinderTypes}
                        inAlert={row.inAlert}
                        cylinderSize={row.cylinderSize}
                    />
                }</div>
            </Table.Cell>

            <Table.Cell width="2">
                {row.device.type !== DEVICE_TYPE.CONCENTRATOR && (
                    <div className="restitution-container flex">
                        {!isSalvusUser(account) && onHover && <Button icon="minus" onClick={onRemoveClicked} />}

                        <div className="restitution">{row.reposition}</div>

                        {!isSalvusUser(account) && onHover && <Button icon="plus" onClick={onAddClicked} />}
                    </div>
                )}
            </Table.Cell>

            {!isSalvusUser(account) && (
                <Table.Cell textAlign="right" width="1">
                    <PopupEditPrescription
                        dispatch={dispatch}
                        patientId={row.id}
                        inAlert={row.inAlert}
                        prescription={row.prescription}
                        onConfirmPopup={onConfirmPrescription}
                        onEditPatient={onEditPatient}
                    />
                </Table.Cell>
            )}
            </Table.Row>
        );
    
}

MonitorTableRow.propTypes = {
    row: PropTypes.object,
    account: PropTypes.object,
};

export default MonitorTableRow;
